import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User, Clock, CreditCard, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AssistantSettingsPage = () => {
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    full_name: '',
    availability: {
      timezone: '',
      workingHours: '',
      daysAvailable: [],
      emergencyAvailable: '',
      availabilityNotes: ''
    },
    payment_info: {
      paymentMethod: '',
      legalName: '',
      handle: '',
      notes: ''
    }
  });

  useEffect(() => {
    const storedAssistant = sessionStorage.getItem('assistant');
    if (!storedAssistant) {
      navigate('/assistant-login');
      return;
    }

    try {
      const assistantData = JSON.parse(storedAssistant);
      setAssistant(assistantData);
      fetchSettings(assistantData.id);
    } catch (e) {
      console.error('Error parsing assistant data:', e);
      navigate('/assistant-login');
    }
  }, [navigate]);

  const fetchSettings = async (assistantId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/settings`, {
        headers: {
          'x-assistant-session': assistantId
        }
      });

      if (res.ok) {
        const data = await res.json();
        setFormData({
          full_name: data.full_name || '',
          availability: data.onboarding_data?.availability || {
            timezone: '',
            workingHours: '',
            daysAvailable: [],
            emergencyAvailable: '',
            availabilityNotes: ''
          },
          payment_info: data.onboarding_data?.payment_info || {
            paymentMethod: '',
            legalName: '',
            handle: '',
            notes: ''
          }
        });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-assistant-session': assistant.id
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        
        // Update session storage with new name
        const updatedAssistant = { ...assistant, full_name: formData.full_name };
        sessionStorage.setItem('assistant', JSON.stringify(updatedAssistant));
        setAssistant(updatedAssistant);
        
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save settings' });
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const updateAvailability = (field, value) => {
    setFormData({
      ...formData,
      availability: { ...formData.availability, [field]: value }
    });
  };

  const updatePaymentInfo = (field, value) => {
    setFormData({
      ...formData,
      payment_info: { ...formData.payment_info, [field]: value }
    });
  };

  const toggleDay = (day) => {
    const current = formData.availability.daysAvailable || [];
    const updated = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day];
    updateAvailability('daysAvailable', updated);
  };

  if (loading || !assistant) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #E5E7EB',
            borderTopColor: '#3B82F6',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
            margin: '0 auto'
          }}></div>
          <p style={{ marginTop: '16px', color: '#6B7280', fontSize: '14px' }}>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{
        background: '#FFFFFF',
        borderBottom: '1px solid #E5E7EB',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px 32px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/a-dashboard')}
            style={{
              padding: '8px',
              background: 'transparent',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ArrowLeft size={20} color="#6B7280" />
          </button>
          <div>
            <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: 0 }}>
              Settings
            </h1>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: '4px 0 0 0' }}>
              Manage your profile and preferences
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px' }}>
        {/* Message Banner */}
        {message.text && (
          <div style={{
            padding: '16px 20px',
            background: message.type === 'success' ? '#F0FDF4' : '#FEF2F2',
            border: `1px solid ${message.type === 'success' ? '#BBF7D0' : '#FCA5A5'}`,
            borderRadius: '12px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertCircle size={20} color={message.type === 'success' ? '#10B981' : '#DC2626'} />
            <span style={{ color: message.type === 'success' ? '#166534' : '#991B1B', fontSize: '14px', fontWeight: '500' }}>
              {message.text}
            </span>
          </div>
        )}

        {/* Profile Section */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <User size={20} color="#3B82F6" />
            <h2 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: 0 }}>
              Profile Information
            </h2>
          </div>

          <div>
            <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
              Display Name
            </label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="Your full name"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '15px'
              }}
            />
            <p style={{ color: '#6B7280', fontSize: '13px', marginTop: '6px' }}>
              This name will be displayed across the dashboard and in messages
            </p>
          </div>
        </div>

        {/* Availability Section */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Clock size={20} color="#8B5CF6" />
            <h2 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: 0 }}>
              Availability & Working Hours
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Time Zone
              </label>
              <select
                value={formData.availability.timezone}
                onChange={(e) => updateAvailability('timezone', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px'
                }}
              >
                <option value="">Select timezone...</option>
                <option value="US/Eastern">US Eastern (ET)</option>
                <option value="US/Central">US Central (CT)</option>
                <option value="US/Mountain">US Mountain (MT)</option>
                <option value="US/Pacific">US Pacific (PT)</option>
                <option value="Europe/London">UK (GMT/BST)</option>
                <option value="Europe/Paris">Central Europe (CET)</option>
                <option value="Asia/Dubai">UAE (GST)</option>
                <option value="Asia/Manila">Philippines (PHT)</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Working Hours
              </label>
              <input
                type="text"
                value={formData.availability.workingHours}
                onChange={(e) => updateAvailability('workingHours', e.target.value)}
                placeholder="e.g., 9:00 AM - 5:00 PM EST"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Days Available
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                  const isSelected = (formData.availability.daysAvailable || []).includes(day);
                  return (
                    <label key={day} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      background: isSelected ? '#EFF6FF' : '#F9FAFB',
                      border: isSelected ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isSelected ? '500' : '400',
                      color: isSelected ? '#1E40AF' : '#374151'
                    }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDay(day)}
                        style={{ cursor: 'pointer' }}
                      />
                      {day}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Emergency Availability
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                {['yes', 'no'].map(option => {
                  const isSelected = formData.availability.emergencyAvailable === option;
                  return (
                    <label key={option} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      background: isSelected ? (option === 'yes' ? '#F0FDF4' : '#FEF2F2') : '#F9FAFB',
                      border: isSelected ? `2px solid ${option === 'yes' ? '#10B981' : '#DC2626'}` : '1px solid #E5E7EB',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isSelected ? '500' : '400',
                      color: isSelected ? (option === 'yes' ? '#166534' : '#991B1B') : '#374151'
                    }}>
                      <input
                        type="radio"
                        checked={isSelected}
                        onChange={() => updateAvailability('emergencyAvailable', option)}
                        style={{ cursor: 'pointer' }}
                      />
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </label>
                  );
                })}
              </div>
              <p style={{ color: '#6B7280', fontSize: '13px', marginTop: '6px' }}>
                Can you respond to urgent matters outside your typical working hours if absolutely necessary?
              </p>
            </div>

            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Additional Notes (Optional)
              </label>
              <textarea
                value={formData.availability.availabilityNotes || ''}
                onChange={(e) => updateAvailability('availabilityNotes', e.target.value)}
                placeholder="Any additional information about your availability..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <CreditCard size={20} color="#F59E0B" />
            <h2 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: 0 }}>
              Payment Preferences
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Payment Method
              </label>
              <select
                value={formData.payment_info.paymentMethod}
                onChange={(e) => updatePaymentInfo('paymentMethod', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px'
                }}
              >
                <option value="">Select payment method...</option>
                <option value="ach">ACH / Bank Transfer</option>
                <option value="paypal">PayPal</option>
                <option value="cashapp">Cash App</option>
                <option value="zelle">Zelle</option>
                <option value="venmo">Venmo</option>
                <option value="other">Other</option>
              </select>
            </div>

            {(formData.payment_info.paymentMethod === 'paypal' || formData.payment_info.paymentMethod === 'cashapp') && (
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                  {formData.payment_info.paymentMethod === 'paypal' ? 'PayPal Email' : 'Cash App Tag'}
                </label>
                <input
                  type="text"
                  value={formData.payment_info.handle || ''}
                  onChange={(e) => updatePaymentInfo('handle', e.target.value)}
                  placeholder={formData.payment_info.paymentMethod === 'paypal' ? 'your@email.com' : '$yourcashtag'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px'
                  }}
                />
              </div>
            )}

            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Legal Name (for payments)
              </label>
              <input
                type="text"
                value={formData.payment_info.legalName}
                onChange={(e) => updatePaymentInfo('legalName', e.target.value)}
                placeholder="Full legal name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Additional Notes (Optional)
              </label>
              <textarea
                value={formData.payment_info.notes || ''}
                onChange={(e) => updatePaymentInfo('notes', e.target.value)}
                placeholder="Any additional payment-related information..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div style={{
          background: '#FFFBEB',
          border: '1px solid #FDE68A',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          display: 'flex',
          gap: '12px'
        }}>
          <AlertCircle size={20} color="#D97706" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{ color: '#92400E', fontSize: '14px', fontWeight: '600', margin: '0 0 6px 0' }}>
              Password & Account Status
            </p>
            <p style={{ color: '#78350F', fontSize: '13px', lineHeight: '1.6', margin: 0 }}>
              For security reasons, password changes and account status can only be managed by administrators. If you need to update your password or have account-related concerns, please contact your assigned executive or BM-Assist administration.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            onClick={() => navigate('/a-dashboard')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              color: '#6B7280',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 24px',
              background: '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantSettingsPage;
